<template>
  <!-- Five stars in the colour of the surrounding text (currentColor): brand
       indigo on white cards, white on the indigo hero and nav. Both clear the
       3:1 graphics contrast PS-123 asks for (about 6.6:1). Each star is filled
       to the value - 4 full plus a 40% partial for 4.4 - and every star keeps a
       full-opacity outline, so the unfilled part still reads as a star. -->
  <svg
    class="rating-stars"
    :viewBox="`0 0 ${width} 24`"
    :style="{ height: `${size}px`, width: `${(size * width) / 24}px` }"
    role="img"
    :aria-label="ariaLabel"
    focusable="false"
  >
    <defs>
      <clipPath
        v-for="star in stars"
        :id="star.clipId"
        :key="star.clipId"
        clipPathUnits="userSpaceOnUse"
      >
        <rect x="0" y="0" :width="24 * star.fill" height="24" />
      </clipPath>
    </defs>
    <!-- The offset sits on the group: a userSpaceOnUse clip resolves in the
         clipped element's own coordinates, so each clip rect starts at 0. -->
    <g
      v-for="star in stars"
      :key="star.clipId"
      :transform="`translate(${star.x} 0)`"
    >
      <path :d="STAR_PATH" fill="currentColor" fill-opacity="0.18" />
      <path
        v-if="star.fill > 0"
        :d="STAR_PATH"
        :clip-path="`url(#${star.clipId})`"
        fill="currentColor"
      />
      <path
        :d="STAR_PATH"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linejoin="round"
      />
    </g>
  </svg>
</template>

<script>
import { RATING_VALUE, RATING_ARIA_LABEL } from '@/helpers/rating'

// The star the site already used for its rating rows, drawn with round joins
// so the points are softened rather than sharp.
const STAR_PATH =
  'M12 3.6l2.5 5.1 5.6.8-4 4 .95 5.6L12 16.4l-5.05 2.7.95-5.6-4-4 5.6-.8L12 3.6z'
const GAP = 3
const COUNT = 5

export default {
  name: 'RatingStars',
  props: {
    // Rendered height in px; the width follows.
    size: {
      type: Number,
      default: 16,
    },
  },
  data() {
    return { STAR_PATH }
  },
  computed: {
    width() {
      return COUNT * 24 + (COUNT - 1) * GAP
    },
    ariaLabel() {
      return RATING_ARIA_LABEL
    },
    stars() {
      return Array.from({ length: COUNT }, (_, i) => {
        // Rounded to tenths so 4.4 gives exactly 0.4, not 0.40000000000000036.
        const fill = Math.min(
          1,
          Math.max(0, Math.round((RATING_VALUE - i) * 10) / 10)
        )
        return {
          x: i * (24 + GAP),
          fill,
          // Unique per instance: the nav, a hero and a card can all be on one
          // page, and duplicate clipPath ids would make them share one clip.
          clipId: `rating-star-${this._uid}-${i}`,
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.rating-stars {
  display: inline-block;
  flex: 0 0 auto;
  vertical-align: middle;
  overflow: visible;
}
</style>
